import { getCDNImage } from "./cdnImage";

/** Inline branded placeholder — always works (no CDN/host dependency). */
const LUXURY_PLACEHOLDER_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="800" height="800" viewBox="0 0 800 800" role="img" aria-label="CheepSweepz premium concierge import"><defs><linearGradient id="bg" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stop-color="#0f172a"/><stop offset="100%" stop-color="#020617"/></linearGradient><linearGradient id="accent" x1="0%" y1="0%" x2="100%" y2="0%"><stop offset="0%" stop-color="#f97316"/><stop offset="100%" stop-color="#fb923c"/></linearGradient></defs><rect width="800" height="800" fill="url(#bg)"/><rect x="48" y="48" width="704" height="704" rx="32" fill="none" stroke="rgba(255,255,255,0.08)" stroke-width="2"/><g transform="translate(400 300)"><circle cx="0" cy="0" r="88" fill="none" stroke="url(#accent)" stroke-width="3" opacity="0.9"/><path d="M-34 8 L0-30 L34 8 Z" fill="none" stroke="#f8fafc" stroke-width="4" stroke-linejoin="round"/><rect x="-42" y="12" width="84" height="56" rx="8" fill="none" stroke="#f8fafc" stroke-width="4"/></g><text x="400" y="500" text-anchor="middle" fill="#f8fafc" font-family="ui-sans-serif,system-ui,sans-serif" font-size="42" font-weight="700" letter-spacing="6">CHEEPSWEEPZ</text><text x="400" y="548" text-anchor="middle" fill="#94a3b8" font-family="ui-sans-serif,system-ui,sans-serif" font-size="22" letter-spacing="4">CONCIERGE</text><text x="400" y="610" text-anchor="middle" fill="#64748b" font-family="ui-sans-serif,system-ui,sans-serif" font-size="18">Premium concierge import</text></svg>`;

export const CHEEP_LUXURY_PLACEHOLDER_DATA_URI =
  `data:image/svg+xml,${encodeURIComponent(LUXURY_PLACEHOLDER_SVG)}`;

/**
 * Master prompt v2.1 / Rule 6: hosted luxury placeholder used by the
 * strict onError wrapper. The remote asset renders quickly and gives a
 * consistent identity across every card on the storefront.
 *
 * URL precedence (first hit wins):
 *   1. window.__CHEEP_HOSTED_LUXURY_PLACEHOLDER (runtime override - lets
 *      Ops flip to https://storage.digitalhustlerscaribbean.com/... the
 *      moment that CDN is provisioned, without a redeploy).
 *   2. <window.origin>/assets/cheepsweepz-luxury-placeholder.svg
 *      (same-origin, always reachable from the satellite domain).
 *   3. Hard-coded same-origin URL on cheepsweepz.shop as the SSR default.
 *
 * The data URI in `CHEEP_LUXURY_PLACEHOLDER_DATA_URI` is the absolute
 * last-resort fallback (no host dependency).
 */
export const CHEEP_HOSTED_LUXURY_PLACEHOLDER = (() => {
  if (typeof window !== "undefined") {
    const override = window.__CHEEP_HOSTED_LUXURY_PLACEHOLDER;
    if (typeof override === "string" && override.startsWith("https://")) {
      return override;
    }
    return `${window.location.origin}/assets/cheepsweepz-luxury-placeholder.svg`;
  }
  return "https://cheepsweepz.shop/assets/cheepsweepz-luxury-placeholder.svg";
})();

/** Alias retained for back-compat with any direct importers. */
export const CHEEP_LUXURY_PLACEHOLDER = CHEEP_HOSTED_LUXURY_PLACEHOLDER;

/**
 * Reject brand assets, stock-photo hosts, and historically-unstable hosts
 * that have leaked into product image fields in the past.
 */
const BRANDED_PLACEHOLDER_MARKERS = [
  "cheepsweepz.shop/logo",
  "cheepsweepz.shop/og-cheepsweepz",
  "cheepsweepz.shop/apple-touch-icon",
  "cheepsweepz.shop/favicon",
  "cheepsweepz-product-card",
  "logo-192.png",
  "logo-256.png",
  "logo-512.png",
  "logo-1024.png",
];
const STOCK_PHOTO_HOSTS = [
  "images.unsplash.com",
  "loremflickr.com",
  "picsum.photos",
  "via.placeholder.com",
  "placeholder.com",
];

function isUsableImageUrl(url) {
  if (!url || typeof url !== "string") return false;
  // Relative storage keys (products/..., locations/...) are usable after CDN expand
  if (
    !url.startsWith("https://") &&
    !url.startsWith("http://") &&
    !url.startsWith("data:image/") &&
    !/^(products|locations|portfolios|product-images)\//i.test(url)
  ) {
    return false;
  }
  const absolute = getCDNImage(url, "");
  if (!absolute) return false;
  if (!(absolute.startsWith("https://") || absolute.startsWith("data:image/") || absolute.startsWith("http://"))) {
    return false;
  }
  const lower = absolute.toLowerCase();
  if (BRANDED_PLACEHOLDER_MARKERS.some((m) => lower.includes(m))) return false;
  if (STOCK_PHOTO_HOSTS.some((h) => lower.includes(h))) return false;
  return true;
}

function pickFirstUsable(list) {
  if (!Array.isArray(list)) return null;
  for (const candidate of list) {
    if (!isUsableImageUrl(candidate)) continue;
    return getCDNImage(candidate, CHEEP_HOSTED_LUXURY_PLACEHOLDER);
  }
  return null;
}

/**
 * Master prompt v2.1 / Rule 6: "Suppress the ✦ Real Product View badge
 * unless the image has been structurally validated against the exact
 * vendor SKU." The API expresses that via `is_verified_image` only after
 * the asset-extractor pipeline produced a SKU-anchored match.
 *
 * We additionally require that the URL itself is non-brand, non-stock, so
 * a stale API response can never resurrect the badge alongside a generic
 * Unsplash photo.
 */
export function hasVerifiedSupplierImage(product) {
  if (!product?.is_verified_image) return false;
  return Boolean(pickFirstUsable(product?.real_images));
}

/** Original IG capture when marketplace gallery hydration was thin. */
export function resolveInstagramSourceImage(product) {
  const direct = product?.instagram_source_image_url;
  if (isUsableImageUrl(direct)) return getCDNImage(direct, CHEEP_HOSTED_LUXURY_PLACEHOLDER);

  const fromAttrs = product?.raw_attributes?.instagram_source_image_url;
  if (isUsableImageUrl(fromAttrs)) {
    return getCDNImage(fromAttrs, CHEEP_HOSTED_LUXURY_PLACEHOLDER);
  }

  return null;
}

/** Prefer verified supplier shots, then API catalog images, then IG social capture, then branded fallback. */
export function resolveListingImage(product) {
  const verified =
    product?.is_verified_image
      ? pickFirstUsable(product?.real_images) || pickFirstUsable(product?.images)
      : null;
  const catalogShot = product?.is_catalog_image
    ? pickFirstUsable(product?.images)
    : null;
  const override = isUsableImageUrl(product?.override_image_url)
    ? getCDNImage(product.override_image_url, CHEEP_HOSTED_LUXURY_PLACEHOLDER)
    : null;
  const fallback =
    pickFirstUsable(product?.real_images) || pickFirstUsable(product?.images);
  const instagramCapture = resolveInstagramSourceImage(product);

  return (
    verified ||
    catalogShot ||
    override ||
    fallback ||
    instagramCapture ||
    CHEEP_HOSTED_LUXURY_PLACEHOLDER
  );
}

export { getCDNImage } from "./cdnImage";
